import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Kleine synchrone state-container voor de platformlaag. Vervangt Akita's Store + Query
 * (Akita wordt sinds 2023 niet meer onderhouden; verwijderd bij de Angular 21-upgrade).
 *
 * Bewust een BehaviorSubject en geen signal: alle consumers zijn Observable-gebaseerd en
 * rekenen erop dat select() synchroon emitteert na update(), zoals bij Akita. Elke select()
 * laat ongewijzigde waarden weg (distinctUntilChanged op referentie). De state wordt niet
 * bevroren (Akita deed dat in dev-mode).
 */
export class StateStore<TState extends object> {
  private readonly state$: BehaviorSubject<TState>;

  constructor(private readonly createInitialState: () => TState) {
    this.state$ = new BehaviorSubject(createInitialState());
  }

  getValue(): TState {
    return this.state$.getValue();
  }

  select(): Observable<TState>;
  select<TResult>(project: (state: TState) => TResult): Observable<TResult>;
  select<TResult>(project?: (state: TState) => TResult): Observable<TState | TResult> {
    const selected$: Observable<TState | TResult> = project
      ? this.state$.pipe(map(project))
      : this.state$.asObservable();
    return selected$.pipe(distinctUntilChanged());
  }

  update(partial: Partial<TState>): void {
    this.setState({ ...this.getValue(), ...partial });
  }

  reset(): void {
    this.setState(this.createInitialState());
  }

  protected setState(state: TState): void {
    this.state$.next(state);
  }
}
